<script>
  /* eslint-disable camelcase */
  import { required } from 'vuelidate/lib/validators';
  import { ToggleButton } from 'vue-js-toggle-button';
  import InputError from '@/_legacy/components/Form/InputError.vue';
  import BigField from '@/_legacy/views/ChecklistBuilder/BigField.vue';
  import SelectControl from '@/_legacy/components/Controls/SelectControl.vue';
  import { ISSUE_PRIORITY } from '@/_legacy/constants';
  import CloseButton from '@/_legacy/components/Modals/CloseButton.vue';
  import PrioritySelector from '@/_legacy/components/Issues/PrioritySelector.vue';

  export default {
    name: 'IssueItemModal',
    components: {
      BigField,
      SelectControl,
      ToggleButton,
      InputError,
      CloseButton,
      PrioritySelector,
    },
    props: {
      issue: {
        type: Object,
        required: false,
      },
      assignees: {
        type: Array,
        required: true,
      },
    },
    data: () => ({
      dueIn: false,
      form: {
        uuid: null,
        title: '',
        description: '',
        assignees: [],
        priority: ISSUE_PRIORITY.MEDIUM,
        suggestedCorrectiveAction: '',
        dueIn: null,
        showInspectorIssueIsRaised: false,
      },
    }),
    validations: {
      form: {
        title: { required },
      },
    },
    mounted() {
      if (!this.issue) {
        return;
      }

      const {
        title,
        description,
        assignees,
        priority,
        suggested_corrective_action,
        due_in,
      } = this.issue.params;

      this.dueIn = !!due_in;
      this.form = {
        uuid: this.issue.uuid,
        title,
        description,
        assignees: assignees.users,
        priority,
        suggestedCorrectiveAction: suggested_corrective_action,
        dueIn: due_in,
        showInspectorIssueIsRaised: this.issue.visible_for_conductor,
      };
    },
    methods: {
      modalClosed() {
        this.$emit('close');
      },

      formSubmitted() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          this.$toasted.show(
            this.$t('This page has form errors, please fix them'),
            { type: 'error' },
          );
        } else {
          this.$emit('confirm', this.form);
        }
      },

      assigneeSelected(assignees) {
        this.form.assignees = assignees || [];
      },

      prioritySelected(priority) {
        this.form.priority = priority;
      },

      dueInClicked() {
        this.form.dueIn = this.dueIn
          ? {
            weeks: 1,
            days: 0,
            hours: 0,
            minutes: 0,
          }
          : null;
      },
    },
  };
</script>

<template>
  <modal>
    <form class="modal-wrapper" @submit.prevent="formSubmitted">
      <CloseButton @click="modalClosed" />

      <div class="modal-content">
        <heading :level="2" class="mb-6">{{ $t('Raise issue') }}</heading>

        <BigField class="mb-4" :label="$t('Issue title')">
          <template slot="field">
            <input
              v-model.trim.lazy="$v.form.title.$model"
              type="text"
              name="title"
              class="w-full form-control form-input form-input-bordered"
            >
          </template>
          <template slot="validationMessage">
            <InputError
              v-if="$v.form.title.$dirty"
              :condition="!$v.form.title.required"
            >
              {{
                $t('The {field} field is required', {
                  field: $t('title'),
                })
              }}
            </InputError>
          </template>
        </BigField>

        <BigField class="mb-4" :label="$t('Description (optional)')">
          <template slot="field">
                        <textarea
                          v-model="form.description"
                          rows="1"
                          class="w-full form-control form-input form-input-bordered h-24"
                          name="description"
                        ></textarea>
          </template>
        </BigField>

        <BigField class="mb-4" :label="$t('Assignee (optional)')">
          <template slot="field">
            <SelectControl
              class="w-full"
              multiple
              :options="assignees"
              :value="form.assignees"
              :placeholder="$t('Select assignees')"
              name="assignee"
              @input="assigneeSelected"
            />
          </template>
        </BigField>

        <BigField class="mb-4" :label="$t('Priority')">
          <template slot="field">
            <PrioritySelector
              v-model="form.priority"
            />
          </template>
        </BigField>

        <div class="my-4">
          <BigField
            class="mb-4 flex justify-between"
            :label="
                            $t('Due after issue has been raised (optional)')
                        "
          >
            <template slot="field">
              <ToggleButton
                v-model="dueIn"
                sync
                class="js-dueIn"
                :width="60"
                :height="26"
                :color="{
                                    checked: 'var(--primary)',
                                    unchecked: '#f2f2f2',
                                    disabled: 'var(--20)'
                                }"
                @change="dueInClicked"
              />
            </template>
          </BigField>

          <div
            v-if="dueIn && form.dueIn"
            class="flex items-center justify-around mt-4"
          >
            <BigField class="mb-4 mr-4" :label="$t('Week(s)')">
              <template slot="field">
                <input
                  v-model="form.dueIn.weeks"
                  type="text"
                  name="weeks"
                  class="w-full form-control form-input form-input-bordered"
                >
              </template>
            </BigField>

            <BigField class="mb-4 mr-4" :label="$t('Day(s)')">
              <template slot="field">
                <input
                  v-model="form.dueIn.days"
                  type="text"
                  name="days"
                  class="w-full form-control form-input form-input-bordered"
                >
              </template>
            </BigField>

            <BigField class="mb-4 mr-4" :label="$t('Hour(s)')">
              <template slot="field">
                <input
                  v-model="form.dueIn.hours"
                  type="text"
                  name="hours"
                  class="w-full form-control form-input form-input-bordered"
                >
              </template>
            </BigField>

            <BigField class="mb-4" :label="$t('Min(s)')">
              <template slot="field">
                <input
                  v-model="form.dueIn.minutes"
                  type="text"
                  name="minutes"
                  class="w-full form-control form-input form-input-bordered"
                >
              </template>
            </BigField>
          </div>
        </div>

        <BigField
          class="mb-4 flex justify-between"
          :label="$t('Show inspector that issue is raised')"
        >
          <template slot="field">
            <ToggleButton
              v-model="form.showInspectorIssueIsRaised"
              sync
              :width="60"
              :height="26"
              :color="{
                                checked: 'var(--primary)',
                                unchecked: '#f2f2f2',
                                disabled: 'var(--20)'
                            }"
            />
          </template>
        </BigField>
      </div>

      <!-- Update Button -->
      <div class="modal-actions-outer-wrapper">
        <div class="modal-actions-inner-wrapper">
          <button
            type="button"
            class="modal-actions-secondary"
            @click.prevent="modalClosed"
          >
            {{ $t('Cancel') }}
          </button>

          <button type="submit" class="modal-actions-primary">
            {{ $t('template-builder.issue.modal.done') }}
          </button>
        </div>
      </div>
    </form>
  </modal>
</template>
